/**
 * AdminSettingsHub (Iter 9.39c)
 *
 * 5-tab Admin Settings page:
 *   - Company       (address, contact, legal entity, logos + where they appear)
 *   - Files         (legal, help, core files - unified grid)
 *   - Access Control 🔒 (magic links + super admin controls - password protected)
 *   - Branding      (default profile pic, org icon, hosting cover - existing content)
 *   - Integrations  (Cloudinary · Beehiiv · Acuity · Sinch · Google Maps)
 *
 * Replaces SettingsSection. SuperAdminPage + legacy-config redirect here.
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Building, FileText, Lock, Palette, Plug, Loader2, Upload, ExternalLink, Link2, Check,
} from "lucide-react";
import { toast } from "sonner";
import LockedTab from "@/components/admin/LockedTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

export default function AdminSettingsHub() {
  const [active, setActive] = useState("company");
  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-settings-hub">
      <h1 className="text-xl font-bold mb-1">Admin Settings</h1>
      <p className="text-xs text-gray-500 mb-4">Company info, legal files, access control, brand defaults, and third-party integrations - all in one place.</p>
      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="company"        data-testid="settings-tab-company"><Building className="w-3.5 h-3.5 mr-1" />Company</TabsTrigger>
          <TabsTrigger value="files"          data-testid="settings-tab-files"><FileText className="w-3.5 h-3.5 mr-1" />Files</TabsTrigger>
          <TabsTrigger value="access"         data-testid="settings-tab-access"><Lock className="w-3.5 h-3.5 mr-1" />Access Control</TabsTrigger>
          <TabsTrigger value="branding"       data-testid="settings-tab-branding"><Palette className="w-3.5 h-3.5 mr-1" />Branding</TabsTrigger>
          <TabsTrigger value="integrations"   data-testid="settings-tab-integrations"><Plug className="w-3.5 h-3.5 mr-1" />Integrations</TabsTrigger>
        </TabsList>

        <TabsContent value="company"      className="mt-4"><CompanyTab /></TabsContent>
        <TabsContent value="files"        className="mt-4"><FilesTab /></TabsContent>
        <TabsContent value="access"       className="mt-4">
          <LockedTab scope="access_control" title="Access Control" description="Protected. Enter admin lock password to manage super-admins and magic links.">
            <AccessControlTab />
          </LockedTab>
        </TabsContent>
        <TabsContent value="branding"     className="mt-4"><BrandingTab /></TabsContent>
        <TabsContent value="integrations" className="mt-4"><IntegrationsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Company ─────────────────────────────────────────────────────
function CompanyTab() {
  const [data, setData] = useState({ name: "", address: "", contact_email: "", legal_entity: "", logo_url: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${API}/company-settings`);
        if (r.ok) { const d = await r.json(); setData((p) => ({ ...p, ...d })); }
      } finally { setLoading(false); }
    };
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/company-settings`, {
        method: "PUT", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (r.ok) toast.success("Saved");
      else toast.error("Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-gray-400" />;

  return (
    <Card>
      <CardContent className="p-6 space-y-4" data-testid="settings-company-tab">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-xs">Company name</Label><Input value={data.name || ""} onChange={(e) => setData({ ...data, name: e.target.value })} data-testid="company-name" /></div>
          <div><Label className="text-xs">Contact email</Label><Input value={data.contact_email || ""} onChange={(e) => setData({ ...data, contact_email: e.target.value })} data-testid="company-email" /></div>
          <div className="md:col-span-2"><Label className="text-xs">Address</Label><Input value={data.address || ""} onChange={(e) => setData({ ...data, address: e.target.value })} data-testid="company-address" /></div>
          <div><Label className="text-xs">Legal entity</Label><Input value={data.legal_entity || ""} onChange={(e) => setData({ ...data, legal_entity: e.target.value })} placeholder="Phygital Inc." data-testid="company-legal-entity" /></div>
          <div><Label className="text-xs">Primary logo URL</Label><Input value={data.logo_url || ""} onChange={(e) => setData({ ...data, logo_url: e.target.value })} data-testid="company-logo" /></div>
        </div>
        <div>
          <h4 className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Logo appears on</h4>
          <ul className="list-disc pl-5 text-xs text-gray-600 space-y-0.5">
            <li>Top navigation (all pages)</li>
            <li>Public envelope covers without a host logo override</li>
            <li>Transactional emails (confirmation, magic link, receipts)</li>
            <li>PDF exports (privacy, terms, pilot agreement)</li>
            <li>Landing page footer</li>
          </ul>
        </div>
        <Button onClick={save} disabled={saving} className="bg-purple-600" data-testid="company-save">
          {saving ? "Saving…" : "Save company settings"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Files ─────────────────────────────────────────────────────
function FilesTab() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${API}/admin/core-files`, { headers: auth() });
        if (r.ok) { const d = await r.json(); setFiles(d.files || d.core_files || []); }
      } finally { setLoading(false); }
    };
    load();
  }, []);
  return (
    <Card>
      <CardContent className="p-6 space-y-3" data-testid="settings-files-tab">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-sm">All platform files</h3>
          <Badge className="bg-purple-100 text-purple-700 border-0">{files.length}</Badge>
        </div>
        <p className="text-xs text-gray-500">Legal (Privacy, Terms, NDA, Pilot agreement), Help center articles, and core engineering files. Open <a href="/admin/docs" className="text-purple-600 underline">docs browser</a> for the full tree.</p>
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : (
          <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
            {files.length === 0 ? (
              <div className="p-4 text-xs text-gray-400">No files yet.</div>
            ) : files.slice(0, 50).map((f) => (
              <div key={f.filename || f.name} className="px-3 py-2 flex items-center gap-2 text-sm" data-testid={`settings-file-${f.filename || f.name}`}>
                <FileText className="w-4 h-4 text-gray-400" />
                <span className="truncate flex-1">{f.filename || f.name}</span>
                {f.size_kb && <span className="text-[11px] text-gray-400">{f.size_kb} KB</span>}
                <a href={`/admin/docs?file=${encodeURIComponent(f.filename || f.name)}`} className="text-xs text-purple-600 hover:underline inline-flex items-center">Open <ExternalLink className="w-3 h-3 ml-0.5" /></a>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Access Control (behind password) ──────────────────────────
function AccessControlTab() {
  const [users, setUsers] = useState([]);
  const [magicLinks, setMagicLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [ua, ml] = await Promise.all([
          fetch(`${API}/admin/users`, { headers: auth() }).then(r => r.ok ? r.json() : { users: [] }),
          fetch(`${API}/admin/magic-links`, { headers: auth() }).then(r => r.ok ? r.json() : { magic_links: [] }),
        ]);
        const u = (ua.users || []).filter((x) => x.role === "super_admin" || x.is_super_admin === true);
        setUsers(u);
        setMagicLinks(ml.magic_links || ml.links || []);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-gray-400" />;

  return (
    <div className="space-y-4" data-testid="settings-access-control-tab">
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Lock className="w-4 h-4" />Super admins</CardTitle>
          <CardDescription>Accounts with full platform admin access. Add / remove via role field on the user record.</CardDescription></CardHeader>
        <CardContent>
          {users.length === 0 ? <p className="text-xs text-gray-400">None</p> : (
            <ul className="divide-y">
              {users.map((u) => (
                <li key={u.user_id} className="py-2 flex items-center gap-2 text-sm">
                  <span className="font-medium">{u.name}</span>
                  <span className="text-gray-500">{u.email}</span>
                  <Badge variant="outline" className="ml-auto text-[10px]">{u.role}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Link2 className="w-4 h-4" />Active magic links</CardTitle>
          <CardDescription>Long-lived login tokens. Generate from an individual user row in People.</CardDescription></CardHeader>
        <CardContent>
          {magicLinks.length === 0 ? <p className="text-xs text-gray-400">None active</p> : (
            <ul className="divide-y max-h-60 overflow-y-auto">
              {magicLinks.slice(0, 20).map((m) => (
                <li key={m.token} className="py-2 flex items-center gap-2 text-sm">
                  <code className="text-[11px] bg-gray-100 px-1.5 py-0.5 rounded">{m.token}</code>
                  <span className="text-gray-600 text-xs truncate">{m.email || m.user_id}</span>
                  <span className="text-[10px] text-gray-400 ml-auto">expires {m.expires_at || "-"}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Branding (preserved from old SettingsSection) ─────────────
function BrandingTab() {
  const [assets, setAssets] = useState({});
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${API}/canonical/ui-assets`, { credentials: "include" });
        if (r.ok) { const d = await r.json(); setAssets(d.assets || {}); }
      } finally { setLoading(false); }
    };
    load();
  }, []);
  const update = async (id, cfg) => {
    await fetch(`${API}/canonical/ui-assets/${id}`, {
      method: "PUT", headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify({ config: cfg }),
    });
    setAssets((p) => ({ ...p, [id]: { ...p[id], config: cfg } }));
    toast.success("Saved");
  };
  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-gray-400" />;
  const pc = assets.default_profile_pic?.config || {};
  const oc = assets.default_org_icon?.config || {};
  return (
    <div className="space-y-4" data-testid="settings-branding-tab">
      <ColorCard title="Default profile picture (initials)" config={pc} onChange={(c) => update("default_profile_pic", c)} testid="branding-profile" />
      <ColorCard title="Default org icon" config={oc} onChange={(c) => update("default_org_icon", c)} testid="branding-org" />
    </div>
  );
}

function ColorCard({ title, config, onChange, testid }) {
  return (
    <Card data-testid={testid}>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-semibold" style={{ backgroundColor: config.background_color || "#000", color: config.text_color || "#fff" }}>MT</div>
          <div className="flex gap-3">
            <div>
              <Label className="text-[11px] text-gray-500">Background</Label>
              <input type="color" value={config.background_color || "#000000"} onChange={(e) => onChange({ ...config, background_color: e.target.value })} className="w-10 h-8 rounded border cursor-pointer" />
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Text</Label>
              <input type="color" value={config.text_color || "#ffffff"} onChange={(e) => onChange({ ...config, text_color: e.target.value })} className="w-10 h-8 rounded border cursor-pointer" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Integrations ─────────────────────────────────────────────
function IntegrationsTab() {
  const integrations = [
    { name: "Cloudinary", status: "active", note: "Image uploads. Keys in backend/.env.", url: "https://cloudinary.com" },
    { name: "Stripe", status: "active", note: "Phygital subscription billing. Not for host payouts.", url: "https://stripe.com" },
    { name: "Acuity", status: "active", note: "Per-host connection via onboarding intake toggle.", url: "https://acuityscheduling.com" },
    { name: "Beehiiv", status: "setup_required", note: "Keys live. 15 automations still to create manually - see /admin/docs?file=BEEHIIV_MASTER.md.", url: "/admin/docs?file=BEEHIIV_MASTER.md" },
    { name: "Sinch / Twilio", status: "active", note: "Magic-link SMS.", url: "https://sinch.com" },
    { name: "Google Maps Places", status: "needs_key", note: "Add GOOGLE_MAPS_API_KEY to frontend/.env for city autocomplete.", url: "https://console.cloud.google.com" },
    { name: "Kommo CRM", status: "not_connected", note: "Planned (F1). IG DM ingest to Inspo folders.", url: "https://kommo.com" },
  ];
  const badge = (s) => {
    const map = {
      active: { label: "Active", cls: "bg-green-100 text-green-700" },
      setup_required: { label: "Setup required", cls: "bg-amber-100 text-amber-700" },
      needs_key: { label: "Needs key", cls: "bg-orange-100 text-orange-700" },
      not_connected: { label: "Not connected", cls: "bg-gray-100 text-gray-600" },
    };
    const m = map[s] || map.not_connected;
    return <Badge className={`${m.cls} border-0 text-[10px]`}>{m.label}</Badge>;
  };
  return (
    <Card>
      <CardContent className="p-6 space-y-3" data-testid="settings-integrations-tab">
        {integrations.map((i) => (
          <div key={i.name} className="flex items-start gap-3 p-3 border rounded-lg" data-testid={`integration-${i.name.toLowerCase().replace(/\W+/g, "-")}`}>
            <Plug className="w-4 h-4 text-gray-400 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{i.name}</span>
                {badge(i.status)}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{i.note}</p>
            </div>
            <a href={i.url} target={i.url.startsWith("/") ? "_self" : "_blank"} rel="noreferrer" className="text-xs text-purple-600 hover:underline inline-flex items-center shrink-0">
              {i.url.startsWith("/") ? "Open guide" : "Open"} <ExternalLink className="w-3 h-3 ml-0.5" />
            </a>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
